/**
 * Pure selectors for the Supplier Onboarding stages.
 *
 * These decide which supplier belongs to which stage. They are deliberately
 * free of React, router and UI imports so both the tab components and the
 * statistic-tile counts can use them without pulling a component tree — and so
 * they can be unit tested without router mocks.
 *
 * They previously lived in CustomerOnboardingTabs.tsx and are re-exported from
 * there for existing importers.
 */
import type {
  OnboardingWorkflow,
  FilledDataset,
  SupplierQualification,
  SupplierInvitation,
} from '../../lib/api';

export interface FilledDatasetWithDetails extends FilledDataset {
  supplier_name?: string;
  dataset_name?: string;
}

/**
 * An invitation as the Invited tab displays it. The tab shows a synthetic
 * "wip" state for a supplier who accepted their invitation but has not yet
 * submitted their questionnaires. That value never comes back from the API, so
 * it is modelled explicitly here rather than cast onto the backend status
 * union — otherwise every consumer believes it is handling a real API status.
 */
export type InvitedStageInvitation = Omit<SupplierInvitation, 'status'> & {
  status: SupplierInvitation['status'] | 'wip';
};

export interface SupplierGroup {
  name: string;
  supplierId?: number;
  reviews: FilledDatasetWithDetails[];
  // CR-5984 Path B: SQ rows for this supplier surface alongside reviews so
  // PDD reflects whatever questionnaire model the tenant uses.
  qualifications: SupplierQualification[];
  // CR-5984: workflow id for orphan rows so the Approve button still
  // has a target if/once the customer assigns questionnaires later.
  orphanWorkflowId?: number;
  // CR-7046: how many questionnaires are ASSIGNED to this supplier, submitted
  // or not. Without it the tab cannot tell "none assigned" from "assigned but
  // not yet returned" — both look empty, and the supplier vanished from the
  // stage entirely (or was mislabelled "Awaiting Questionnaire Assignment").
  assignedCount?: number;
  /** Questionnaires already approved/rejected. Distinguishes "never assigned"
   *  from "all done" — both leave assignedCount at 0, and conflating them told
   *  a reviewer who had just approved a questionnaire that none was assigned. */
  settledCount?: number;
  /** CR-6513: of the assigned questionnaires, how many the supplier has started
   *  but not submitted. `assignedCount` alone cannot separate 'invited' from
   *  'in_progress', so a supplier already part-way through read as though they
   *  had not begun. */
  inProgressCount?: number;
}

/**
 * CR-5953 rework 2: the single source of truth for the Pending Due Diligence
 * supplier list. Groups submitted questionnaires (FilledDataset),
 * SupplierQualifications, and orphan workflows by supplier — keyed by name so
 * a supplier with several questionnaires renders as one card. The tab list, the
 * tab badge and (CR-5965) the statistic tile all derive from this, so none of
 * them can disagree with the number of cards shown (the QA "3 vs 5" defect came
 * from the badge counting supplier IDs while the list grouped by name).
 *
 * @param pendingReviews - filled datasets awaiting customer action
 * @param submittedQualifications - legacy SupplierQualification rows
 * @param orphanWorkflows - post-acceptance workflows with no questionnaires
 * @returns one entry per distinct supplier card the tab renders
 */
export function groupPendingDueDiligenceSuppliers(
  pendingReviews: FilledDatasetWithDetails[],
  submittedQualifications: SupplierQualification[] = [],
  orphanWorkflows: OnboardingWorkflow[] = [],
  // CR-6513: workflows used ONLY to fill in counts on cards that already
  // exist. `orphanWorkflows` cannot do this job: deriveOrphanWorkflows drops
  // any supplier that has questionnaires, so a supplier with one submitted and
  // two still outstanding never reached the merge branch below and their card
  // showed "1 questionnaire submitted" with no hint that two remained.
  // Deliberately never creates a group — that would re-list a supplier still
  // in the Invited or Pending Approval stage.
  countWorkflows: OnboardingWorkflow[] = [],
): SupplierGroup[] {
  const grouped: Record<string, SupplierGroup> = {};
  for (const review of pendingReviews) {
    const key = review.supplier_name || `supplier-${review.supplier}`;
    if (!grouped[key]) {
      grouped[key] = { name: review.supplier_name || 'Unknown Supplier', supplierId: review.supplier, reviews: [], qualifications: [] };
    }
    grouped[key].reviews.push(review);
  }
  for (const q of submittedQualifications) {
    const key = q.supplier_name || `supplier-${q.supplier}`;
    if (!grouped[key]) {
      grouped[key] = { name: q.supplier_name || 'Unknown Supplier', supplierId: q.supplier, reviews: [], qualifications: [] };
    }
    grouped[key].qualifications.push(q);
  }
  for (const w of orphanWorkflows) {
    const key = w.supplier_name || `supplier-${w.supplier}`;
    const assignedCount = workflowAssignedCount(w);
    const settledCount = workflowSettledCount(w);
    const inProgressCount = workflowInProgressCount(w);
    if (grouped[key]) {
      // CR-7046: already listed via submitted questionnaires — still record how
      // many were assigned so the card can say "1 of 3 submitted".
      grouped[key].assignedCount = Math.max(grouped[key].assignedCount ?? 0, assignedCount);
      grouped[key].settledCount = Math.max(grouped[key].settledCount ?? 0, settledCount);
      grouped[key].inProgressCount = Math.max(grouped[key].inProgressCount ?? 0, inProgressCount);
      continue;
    }
    grouped[key] = {
      name: w.supplier_name || 'Unknown Supplier',
      supplierId: w.supplier,
      reviews: [],
      qualifications: [],
      orphanWorkflowId: w.id,
      assignedCount,
      settledCount,
      inProgressCount,
    };
  }
  // Counts only, on groups that already exist. Match on supplier id first: a
  // workflow's supplier record and a qualification's supplier_name can differ
  // once a supplier is renamed, and a name-only match silently misses those.
  const byId = new Map<number, SupplierGroup>();
  for (const g of Object.values(grouped)) {
    if (g.supplierId != null) byId.set(g.supplierId, g);
  }
  for (const w of countWorkflows) {
    const g = (w.supplier != null ? byId.get(w.supplier) : undefined)
      ?? grouped[w.supplier_name || `supplier-${w.supplier}`];
    if (!g) continue;
    g.assignedCount = Math.max(g.assignedCount ?? 0, workflowAssignedCount(w));
    g.settledCount = Math.max(g.settledCount ?? 0, workflowSettledCount(w));
    g.inProgressCount = Math.max(g.inProgressCount ?? 0, workflowInProgressCount(w));
  }
  return Object.values(grouped);
}

/**
 * CR-5984 / CR-5953 rework 2: derive the "orphan" workflows that surface in
 * Pending Due Diligence as "Awaiting Questionnaire Assignment" — suppliers in a
 * post-acceptance workflow state with no FilledDatasets yet. They must be
 * excluded when the supplier is already represented in another stage, otherwise
 * one supplier shows in two tabs (QA: a supplier appeared in both Pending
 * Approvals and Pending Due Diligence):
 *   - has SupplierQualifications (questionnaire path wins)
 *   - is in the Invited stage (CR-5953)
 *   - is in the Pending-Approvals stage (CR-5953 rework 2 — the missing exclusion)
 */
export const PDD_ORPHAN_WORKFLOW_STATUSES = new Set([
  'registered', 'documents_pending', 'questionnaires_pending',
  'submitted', 'under_review', 'more_info_required',
]);

/** CR-7046: assigned-questionnaire count carried on the workflow payload.
 *
 * Both models count. Helix assigns questionnaires as onboarding FilledDatasets
 * OR as core SupplierQualifications, and this tab renders both — but only the
 * first was counted here, so the card compared an assigned figure drawn from
 * one model against a submitted figure drawn from both. A supplier holding
 * only qualifications read "Awaiting Questionnaire Assignment" while looking
 * at the questionnaires it had been assigned.
 */
export function workflowSettledCount(w: OnboardingWorkflow): number {
  return (w as OnboardingWorkflow & { qualification_settled_count?: number })
    .qualification_settled_count ?? 0;
}

export function workflowInProgressCount(w: OnboardingWorkflow): number {
  // Sums both questionnaire models, mirroring workflowAssignedCount. Counting
  // only one would leave every tenant on the other model permanently reading
  // "awaiting submission", however far through their questionnaires they were.
  const x = w as OnboardingWorkflow & {
    qualification_in_progress_count?: number;
    filled_dataset_in_progress_count?: number;
  };
  return (x.qualification_in_progress_count ?? 0)
    + (x.filled_dataset_in_progress_count ?? 0);
}

export function workflowAssignedCount(w: OnboardingWorkflow): number {
  const x = w as OnboardingWorkflow & {
    filled_dataset_count?: number;
    qualification_count?: number;
  };
  return (x.filled_dataset_count ?? 0) + (x.qualification_count ?? 0);
}

export function deriveOrphanWorkflows(
  workflows: OnboardingWorkflow[],
  opts: {
    suppliersWithQualifications: Set<number>;
    invitedStageSupplierIds: Set<number>;
    pendingApprovalSupplierIds: Set<number>;
    matchesSearch: (name?: string) => boolean;
  },
): OnboardingWorkflow[] {
  // CR-7046: previously required filled_dataset_count === 0, which dropped
  // every supplier whose questionnaires had been assigned but not yet
  // submitted — they matched no other PDD source either, so they rendered
  // nowhere. The count is now carried through instead of used as a filter.
  return workflows.filter(w =>
    PDD_ORPHAN_WORKFLOW_STATUSES.has(w.status) &&
    !opts.suppliersWithQualifications.has(w.supplier) &&
    !opts.invitedStageSupplierIds.has(w.supplier) &&
    !opts.pendingApprovalSupplierIds.has(w.supplier) &&
    opts.matchesSearch(w.supplier_name)
  );
}
